import { Subject, Subscription } from 'rxjs';

import type { DataClientFetchWatcher, FetchWatcher } from './api';
import {
  denormalizeFromState,
  isDataClientFetchWatcher,
  resolveDenormalized,
} from './api';
import { DataClientController } from './data-client-controller';

export type DataFetcherParams = {
  search?: string;
  sort: Record<string, string | null | undefined>;
  page: number;
};
type DataFetcher<AsyncConnectorResult> = ({
  sort,
  page,
}: DataFetcherParams) => FetchWatcher<AsyncConnectorResult>;

export type DataParserResponse = {
  currentPage: number;
  totalPages: number;
  data: unknown[] | undefined | null;
};

type DataParser<AsyncConnectorResult> = (
  response: AsyncConnectorResult
) => DataParserResponse;

/** A derived, paginated view of the rows the connector is tracking. */
export type AsyncConnectorView<Row> = {
  items: Row[];
  hasMore: boolean;
  currentPage: number;
  totalPages: number;
};

type PageDescriptor = Pick<DataClientFetchWatcher, '__endpoint' | '__args'>;

/**
 * Paginated wrapper over a read descriptor. Public shape
 * (`getData` / `onInvalidate` / `parseData` / `getPageSize` /
 * `hasFetched` / `cleanup`) preserved from the legacy
 * FetchWatcher-based implementation.
 *
 * The `dataFetcher` callback may return either flavor of `FetchWatcher`:
 *   - **Legacy** (no schema) → `getData` calls `refetch` / `toPromise`
 *     and `onInvalidate` subscribes via `listenForInvalidate`, exactly
 *     like the original implementation.
 *   - **Data Client** (with schema) → `getData` runs through
 *     `controller.fetch` (always-fire is correct here — `getData` is
 *     called imperatively per page, not in a render path). `onInvalidate`
 *     is a no-op for now; DataGrids using a Data Client-backed fetcher
 *     get fresh data on refetch via the cache, but don't get imperative
 *     invalidate callbacks. Tracked for follow-up.
 *
 * Opt-in store-derived list surface (`reload` / `loadMore` / `getView` /
 * `subscribe`): lets a consumer render a paginated list whose rows are READ
 * FROM the Data Client store, not held as a separate copy. Each fetched page is
 * normalized into the store (via the same `getData` path) and the connector
 * keeps only the page descriptors + paging meta; `getView(state)` re-derives the
 * rows from the supplied store state. So a normal `Api.write` (PATCH/PUT/POST)
 * that merges into the same entities shows up on the next store commit with NO
 * refetch and NO mutation bookkeeping in the consumer — the connector just
 * watches the store. When no Data Client controller is registered (e.g. unit
 * tests on the legacy path), it falls back to the parsed page rows so rendering
 * still works. These methods do nothing for the existing `getData`-only
 * consumers (DataGrid, Dropdown), which remain fully backward compatible.
 */
export class AsyncConnector<AsyncConnectorResult, Row = unknown> {
  private dataFetcher: DataFetcher<AsyncConnectorResult>;

  private dataParser: DataParser<AsyncConnectorResult>;

  private pageSize: number;

  private watcher?: FetchWatcher<AsyncConnectorResult>;

  private fetchCount = 0;

  private onUnmountCallback?: () => void;

  // Page descriptors for store-derived reads (Data Client path).
  private pageDescriptors: PageDescriptor[] = [];

  // Parsed page rows; the fallback source when no Data Client store is present.
  private fallbackRows: Row[] = [];

  private currentPage = 0;

  private totalPages = 0;

  private searchParam?: string;

  private sortParam: DataFetcherParams['sort'] = {};

  // Bumped on every reload; in-flight fetches whose token no longer matches are
  // discarded so a slow page-1 response can't clobber a newer search.
  private requestSeq = 0;

  private isLoadingMore = false;

  // Bumped whenever the tracked pages / paging meta change, so subscribers
  // re-render even when the store state reference is unchanged.
  private version = 0;

  private changes = new Subject<void>();

  private memoState: unknown;

  private memoVersion = -1;

  private memoView?: AsyncConnectorView<Row>;

  constructor({
    getData,
    parseData,
    pageSize = 20,
    onUnmount,
  }: {
    getData: DataFetcher<AsyncConnectorResult>;
    parseData: DataParser<AsyncConnectorResult>;
    pageSize?: number;
    onUnmount?: () => void;
  }) {
    this.dataFetcher = getData;
    this.dataParser = parseData;
    this.pageSize = pageSize;
    this.onUnmountCallback = onUnmount;
  }

  async getData(args: DataFetcherParams): Promise<AsyncConnectorResult> {
    const watcher = this.dataFetcher(args);
    this.watcher = watcher;

    if (isDataClientFetchWatcher(watcher)) {
      const data = (await resolveDenormalized(
        watcher.__endpoint,
        ...watcher.__args
      )) as AsyncConnectorResult;
      this.fetchCount += 1;
      return data;
    }

    const data = await (watcher.hasResolved()
      ? watcher.refetch(false)
      : watcher.toPromise());
    this.fetchCount += 1;
    return data;
  }

  onInvalidate(evt: () => void): Subscription | undefined {
    if (!this.watcher) return undefined;
    if (isDataClientFetchWatcher(this.watcher)) {
      // No-op for Data Client watchers. See class docblock.
      return new Subscription();
    }
    return this.watcher.listenForInvalidate(evt);
  }

  parseData(response: AsyncConnectorResult) {
    return this.dataParser(response);
  }

  getPageSize() {
    return this.pageSize;
  }

  hasFetched() {
    return this.fetchCount > 0;
  }

  cleanup() {
    this.onUnmountCallback?.();
  }

  /**
   * Reset and fetch page 1 with the given search/sort. Stale in-flight reloads
   * (superseded by a newer call) are discarded.
   */
  reload = async (params?: {
    search?: string;
    sort?: DataFetcherParams['sort'];
  }): Promise<void> => {
    this.requestSeq += 1;
    const seq = this.requestSeq;
    const search = params?.search;
    const sort = params?.sort ?? {};

    const fetched = await this.fetchPage({ search, sort, page: 1 });
    if (seq !== this.requestSeq) return;

    this.searchParam = search;
    this.sortParam = sort;
    this.pageDescriptors = fetched.descriptor ? [fetched.descriptor] : [];
    this.fallbackRows = fetched.rows;
    this.currentPage = fetched.parsed.currentPage;
    this.totalPages = fetched.parsed.totalPages;
    this.bump();
  };

  /** Fetch and append the next page using the search/sort from the last reload. */
  loadMore = async (): Promise<void> => {
    if (this.isLoadingMore || this.currentPage >= this.totalPages) return;
    this.isLoadingMore = true;
    const seq = this.requestSeq;
    try {
      const fetched = await this.fetchPage({
        search: this.searchParam,
        sort: this.sortParam,
        page: this.currentPage + 1,
      });
      if (seq !== this.requestSeq) return;

      if (fetched.descriptor) {
        this.pageDescriptors = [...this.pageDescriptors, fetched.descriptor];
      }
      this.fallbackRows = [...this.fallbackRows, ...fetched.rows];
      this.currentPage = fetched.parsed.currentPage;
      this.totalPages = fetched.parsed.totalPages;
      this.bump();
    } finally {
      this.isLoadingMore = false;
    }
  };

  /**
   * Re-derive the paginated rows from the supplied store state. With a Data
   * Client controller, rows come from the normalized store (so writes that
   * merged into the same entities are reflected); otherwise the parsed page rows
   * are used. Memoized by (state, version) so the returned reference is stable
   * between changes — safe to drive a render.
   */
  getView = (state: unknown): AsyncConnectorView<Row> => {
    if (
      this.memoView &&
      state === this.memoState &&
      this.version === this.memoVersion
    ) {
      return this.memoView;
    }

    const items =
      DataClientController.has() && this.pageDescriptors.length > 0
        ? this.pageDescriptors.flatMap(descriptor => {
            // `denormalizeFromState` returns the denormalized RESPONSE; run it
            // through `parseData` to extract the rows, same as the fetch path.
            const response = denormalizeFromState<AsyncConnectorResult>(
              descriptor,
              state
            );
            return (this.parseData(response).data ?? []) as Row[];
          })
        : this.fallbackRows;

    const view: AsyncConnectorView<Row> = {
      items,
      hasMore: this.currentPage < this.totalPages,
      currentPage: this.currentPage,
      totalPages: this.totalPages,
    };
    this.memoState = state;
    this.memoVersion = this.version;
    this.memoView = view;
    return view;
  };

  /** Subscribe to paging/meta changes; returns an unsubscribe fn. */
  subscribe = (onChange: () => void): (() => void) => {
    const sub = this.changes.subscribe(onChange);
    return () => {
      sub.unsubscribe();
    };
  };

  private async fetchPage(args: DataFetcherParams): Promise<{
    descriptor: PageDescriptor | null;
    rows: Row[];
    parsed: DataParserResponse;
  }> {
    const watcher = this.dataFetcher(args);
    this.watcher = watcher;
    this.fetchCount += 1;

    if (isDataClientFetchWatcher(watcher)) {
      const response = (await resolveDenormalized(
        watcher.__endpoint,
        ...watcher.__args
      )) as AsyncConnectorResult;
      const parsed = this.parseData(response);
      return {
        descriptor: { __endpoint: watcher.__endpoint, __args: watcher.__args },
        rows: (parsed.data ?? []) as Row[],
        parsed,
      };
    }

    const response = await (watcher.hasResolved()
      ? watcher.refetch(false)
      : watcher.toPromise());
    const parsed = this.parseData(response);
    return { descriptor: null, rows: (parsed.data ?? []) as Row[], parsed };
  }

  private bump() {
    this.version += 1;
    this.changes.next();
  }
}
